import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BrowserRouter,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AppsIcon from "@mui/icons-material/Apps";
import BugReportIcon from "@mui/icons-material/BugReport";
import SettingsIcon from "@mui/icons-material/Settings";
import "./App.css";

import {
  createItem,
  createLocation,
  deleteHistoricItemName,
  deleteItem,
  deleteLocation,
  getItems,
  getHistoricItemNames,
  getLocations,
  importLocationsCsv,
  isApiDebugEnabled,
  setApiDebugEnabled,
  updateItem,
  updateLocation,
} from "./api/palletApi";
import { AddedLocationModal } from "./components/AddedLocationModal";
import { ActionModal } from "./components/ActionModal";
import { ActiveItemPanel } from "./components/ActiveItemPanel";
import { ConfirmModal } from "./components/ConfirmModal";
import { DebugDiagnosticsModal } from "./components/DebugDiagnosticsModal";
import { HistoricItemNamesModal } from "./components/HistoricItemNamesModal";
import { ImportModal } from "./components/ImportModal";
import { ItemList } from "./components/ItemList";
import { LocationPicker } from "./components/LocationPicker";
import { NoteModal } from "./components/NoteModal";
import { PartialModal } from "./components/PartialModal";
import { ProdNeededModal } from "./components/ProdNeededModal";
import { SettingsModal } from "./components/SettingsModal";
import { Toast } from "./components/Toast";
import { ViewAll } from "./pages/ViewAll";
import { useLocalStorage } from "./hooks/useLocalStorage";
import { formatLocation, formatLocationLong } from "./utils/locations";
import { formatProdNeededCode } from "./utils/prodNeeded";

function isDefaultItemName(itemName) {
  return /^ITEM\s+\d+$/i.test(itemName.trim());
}

function AppShell() {
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [historicItemNames, setHistoricItemNames] = useState([]);
  const [activeItemId, setActiveItemId] = useLocalStorage(
    "palletPicker.activeItemId",
    null
  );
  const [selectedRow, setSelectedRow] = useLocalStorage(
    "palletPicker.selectedRow",
    null
  );
  const [selectedBay, setSelectedBay] = useLocalStorage(
    "palletPicker.selectedBay",
    null
  );
  const [rowButtonsPerRow, setRowButtonsPerRow] = useLocalStorage(
    "palletPicker.rowButtonsPerRow",
    5
  );
  const [bayButtonsPerRow, setBayButtonsPerRow] = useLocalStorage(
    "palletPicker.bayButtonsPerRow",
    4
  );
  const [showEmptyRows, setShowEmptyRows] = useLocalStorage(
    "palletPicker.showEmptyRows",
    false
  );
  const [, setLastView] = useLocalStorage("palletPicker.lastView", "modify");
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");
  const [error, setError] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [modalMode, setModalMode] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerInitialTab, setPickerInitialTab] = useState("row");
  const [locationIsHardline, setLocationIsHardline] = useState(false);
  const [activeItemVisible, setActiveItemVisible] = useState(false);
  const [activeLocationId, setActiveLocationId] = useState(null);
  const [addedLocation, setAddedLocation] = useState(null);
  const [highlightedItemId, setHighlightedItemId] = useState(null);
  const [highlightedLocationId, setHighlightedLocationId] = useState(null);
  const [editSignal, setEditSignal] = useState(0);
  const [pendingDeleteItem, setPendingDeleteItem] = useState(null);
  const [historicNamesLoading, setHistoricNamesLoading] = useState(false);
  const [historicNameMode, setHistoricNameMode] = useState("rename");
  const [debugEnabled, setDebugEnabledState] = useState(() => isApiDebugEnabled());
  const activePanelRef = useRef(null);
  const pickerRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  const activeItem = useMemo(
    () => items.find((item) => item.id === Number(activeItemId)) || null,
    [activeItemId, items]
  );

  const visibleActiveItem = activeItemVisible ? activeItem : null;

  const activeItemLocations = useMemo(
    () =>
      visibleActiveItem
        ? locations.filter((candidate) => candidate.item_id === visibleActiveItem.id)
        : [],
    [visibleActiveItem, locations]
  );

  const showToast = useCallback((message) => {
    setToast(message);
  }, []);

  const setDebugEnabled = useCallback((enabled) => {
    setApiDebugEnabled(enabled);
    setDebugEnabledState(enabled);
  }, []);

  const scrollToRef = useCallback((ref) => {
    window.setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  }, []);

  const pulseItem = useCallback((id) => {
    setHighlightedItemId(id);
    window.setTimeout(() => setHighlightedItemId(null), 1800);
  }, []);

  const pulseLocation = useCallback((id) => {
    setHighlightedLocationId(id);
    window.setTimeout(() => setHighlightedLocationId(null), 1800);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [itemsData, locationsData, historicNamesData] = await Promise.all([
        getItems(),
        getLocations(),
        getHistoricItemNames(),
      ]);
      setItems(itemsData);
      setLocations(locationsData);
      setHistoricItemNames(historicNamesData);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshHistoricItemNames = useCallback(async () => {
    setHistoricNamesLoading(true);
    try {
      const names = await getHistoricItemNames();
      setHistoricItemNames(names);
      return names;
    } catch (requestError) {
      setError(requestError.message);
      return null;
    } finally {
      setHistoricNamesLoading(false);
    }
  }, []);

  useEffect(() => {
    const debugParam = new URLSearchParams(window.location.search).get("debug");
    if (debugParam === "1") {
      setDebugEnabled(true);
    }
    if (debugParam === "0") {
      setDebugEnabled(false);
    }
  }, [setDebugEnabled]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const view = location.pathname === "/all" ? "all" : "modify";
    setLastView(view);
  }, [location.pathname, setLastView]);

  useEffect(() => {
    if (!toast) {
      return undefined;
    }
    const timer = window.setTimeout(() => setToast(""), 3200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (activeItemVisible && !activeItem) {
      setActiveItemVisible(false);
      setPickerOpen(false);
    }
  }, [activeItem, activeItemVisible]);

  const handleViewAll = () => {
    setLastView("all");
    navigate("/all");
  };

  const handleModify = () => {
    setLastView("modify");
    navigate("/");
  };

  const handleSelectItem = (itemId) => {
    setActiveItemId(itemId);
    setActiveItemVisible(true);
    setPickerOpen(false);
    setPickerInitialTab("row");
    setSelectedRow(null);
    setSelectedBay(null);
    setLocationIsHardline(false);
    setActiveLocationId(null);
    pulseItem(itemId);
    scrollToRef(activePanelRef);
  };

  const handleCreateItem = async (itemName) => {
    try {
      const item = await createItem(itemName);
      setItems((current) => [...current, item]);
      setActiveItemId(item.id);
      setActiveItemVisible(true);
      setPickerOpen(false);
      setPickerInitialTab("row");
      setSelectedRow(null);
      setSelectedBay(null);
      setLocationIsHardline(false);
      setActiveLocationId(null);
      setEditSignal((current) => current + 1);
      pulseItem(item.id);
      scrollToRef(activePanelRef);
      await refreshHistoricItemNames();
      return item;
    } catch (requestError) {
      setError(requestError.message);
      return null;
    }
  };

  const handleRenameItem = async (itemName) => {
    if (!visibleActiveItem) {
      return null;
    }
    try {
      const item = await updateItem(visibleActiveItem.id, { item_name: itemName });
      setItems((current) =>
        current.map((candidate) => (candidate.id === item.id ? item : candidate))
      );
      setLocations((current) =>
        current.map((candidate) =>
          candidate.item_id === item.id
            ? { ...candidate, item_name: item.item_name }
            : candidate
        )
      );
      showToast(`Renamed item to ${item.item_name}`);
      await refreshHistoricItemNames();
      return item;
    } catch (requestError) {
      setError(requestError.message);
      return null;
    }
  };

  const handleUpdateProdNeeded = async (prodNeeded) => {
    if (!visibleActiveItem) {
      return null;
    }
    try {
      const item = await updateItem(visibleActiveItem.id, {
        prod_needed: prodNeeded,
      });
      setItems((current) =>
        current.map((candidate) => (candidate.id === item.id ? item : candidate))
      );
      showToast(
        item.prod_needed
          ? `Prod Needed set to ${formatProdNeededCode(item.prod_needed)}`
          : "Prod Needed cleared"
      );
      return item;
    } catch (requestError) {
      setError(requestError.message);
      return null;
    }
  };

  const handleDeleteItem = async (itemToDelete) => {
    if (!itemToDelete) {
      return;
    }

    const deletedItem = itemToDelete;
    try {
      await deleteItem(deletedItem.id);
      setItems((current) => current.filter((item) => item.id !== deletedItem.id));
      setLocations((current) =>
        current.filter((candidate) => candidate.item_id !== deletedItem.id)
      );
      if (deletedItem.id === Number(activeItemId)) {
        setActiveItemId(null);
        setActiveItemVisible(false);
        setPickerOpen(false);
        setPickerInitialTab("row");
        setSelectedRow(null);
        setSelectedBay(null);
        setLocationIsHardline(false);
        setActiveLocationId(null);
      }
      setSelectedLocation(null);
      setAddedLocation(null);
      setPendingDeleteItem(null);
      setModalMode(null);
      showToast(`Deleted ${deletedItem.item_name}`);
      await refreshHistoricItemNames();
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const handleRequestDeleteItem = async (itemToDelete) => {
    const itemLocations = locations.filter(
      (candidate) => candidate.item_id === itemToDelete.id
    );
    if (itemLocations.length === 0 && isDefaultItemName(itemToDelete.item_name)) {
      await handleDeleteItem(itemToDelete);
      return;
    }
    setPendingDeleteItem(itemToDelete);
    setModalMode("delete-item");
  };

  const handleDeleteAllItems = async () => {
    try {
      await Promise.all(items.map((item) => deleteItem(item.id)));
      setItems([]);
      setLocations([]);
      setActiveItemId(null);
      setActiveItemVisible(false);
      setPickerOpen(false);
      setPickerInitialTab("row");
      setSelectedRow(null);
      setSelectedBay(null);
      setLocationIsHardline(false);
      setSelectedLocation(null);
      setAddedLocation(null);
      setActiveLocationId(null);
      setModalMode(null);
      showToast("Deleted all items");
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const handleDeleteAllLocations = async () => {
    try {
      await Promise.all(locations.map((locationRecord) => deleteLocation(locationRecord.id)));
      setLocations([]);
      setPickerOpen(false);
      setPickerInitialTab("row");
      setSelectedRow(null);
      setSelectedBay(null);
      setLocationIsHardline(false);
      setSelectedLocation(null);
      setAddedLocation(null);
      setActiveLocationId(null);
      setPendingDeleteItem(null);
      setModalMode(null);
      showToast("Deleted all locations");
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const handleSaveLocation = async () => {
    if (!visibleActiveItem || !selectedRow || !selectedBay) {
      return;
    }
    const savedRow = selectedRow;
    const savedBay = selectedBay;
    const savedHardline = locationIsHardline;
    try {
      const locationRecord = await createLocation(visibleActiveItem.id, {
        row_number: savedRow,
        bay_number: savedBay,
        is_hardline: savedHardline,
      });
      setLocations((current) => [...current, locationRecord]);
      setActiveLocationId(locationRecord.id);
      setSelectedRow(null);
      setSelectedBay(null);
      setPickerOpen(false);
      setPickerInitialTab("row");
      pulseLocation(locationRecord.id);
      scrollToRef(activePanelRef);
      setAddedLocation(locationRecord);
      setModalMode("added-location");
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const handlePatchLocation = async (id, fields, successMessage) => {
    try {
      const updated = await updateLocation(id, fields);
      setLocations((current) =>
        current.map((candidate) => (candidate.id === updated.id ? updated : candidate))
      );
      setSelectedLocation(updated);
      showToast(successMessage);
      return updated;
    } catch (requestError) {
      setError(requestError.message);
      return null;
    }
  };

  const handleRemoveLocation = async () => {
    if (!selectedLocation) {
      return;
    }
    try {
      await deleteLocation(selectedLocation.id);
      setLocations((current) =>
        current.filter((candidate) => candidate.id !== selectedLocation.id)
      );
      if (selectedLocation.id === Number(activeLocationId)) {
        setActiveLocationId(null);
      }
      showToast(
        `Removed ${formatLocation(selectedLocation)} from ${selectedLocation.item_name}`
      );
      setSelectedLocation(null);
      setModalMode(null);
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const handleImportCsv = async (file, dryRun) => {
    const summary = await importLocationsCsv(file, dryRun);
    if (!dryRun) {
      await loadData();
      showToast(`Imported ${summary.rows} CSV rows`);
    }
    return summary;
  };

  const handleNewLocation = () => {
    setPickerOpen(true);
    setPickerInitialTab("row");
    setSelectedRow(null);
    setSelectedBay(null);
    setLocationIsHardline(false);
    window.setTimeout(() => {
      pickerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  const reopenPicker = useCallback((nextInitialTab) => {
    setPickerOpen(true);
    setPickerInitialTab(nextInitialTab);
    window.setTimeout(() => {
      pickerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  }, []);

  const closeAddedLocationModal = () => {
    setModalMode(null);
    setAddedLocation(null);
  };

  const handleAddAnotherRow = () => {
    const keepHardline = Boolean(addedLocation?.is_hardline);
    closeAddedLocationModal();
    setSelectedRow(null);
    setSelectedBay(null);
    setLocationIsHardline(keepHardline);
    reopenPicker("row");
  };

  const handleAddAnotherBay = () => {
    if (!addedLocation) {
      return;
    }
    const keepHardline = Boolean(addedLocation.is_hardline);
    const row = addedLocation.row_number;
    closeAddedLocationModal();
    setSelectedRow(row);
    setSelectedBay(null);
    setLocationIsHardline(keepHardline);
    reopenPicker("bay");
  };

  const handleAddNewItem = async () => {
    const item = await handleCreateItem();
    if (item) {
      closeAddedLocationModal();
    }
  };

  const openHistoricNames = (mode) => {
    setHistoricNameMode(mode);
    setModalMode("historic-names");
    refreshHistoricItemNames();
  };

  const closeHistoricNames = () => {
    setModalMode(
      historicNameMode === "create" && addedLocation ? "added-location" : null
    );
  };

  const handleSelectHistoricName = async (record) => {
    const item =
      historicNameMode === "create"
        ? await handleCreateItem(record.item_name)
        : await handleRenameItem(record.item_name);
    if (!item) {
      return;
    }
    setModalMode(null);
    if (historicNameMode === "create") {
      setAddedLocation(null);
    }
  };

  const handleDeleteHistoricName = async (record) => {
    try {
      await deleteHistoricItemName(record.id);
      setHistoricItemNames((current) =>
        current.filter((candidate) => candidate.id !== record.id)
      );
      showToast(`Removed ${record.item_name} from saved names`);
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const handleOpenSettings = () => {
    setModalMode("settings");
  };

  const saveDisabled = !visibleActiveItem || !selectedRow || !selectedBay;

  return (
    <main className="app-shell">
      <div className="app-container">
        <Routes>
          <Route
            path="/"
            element={
              <>
                <div
                  className={`top-actions ${debugEnabled ? "debug-enabled" : ""}`}
                  aria-label="Primary actions"
                >
                  <button
                    className="icon-button top-view-button"
                    type="button"
                    aria-label="View all"
                    onClick={handleViewAll}
                  >
                    <AppsIcon />
                  </button>
                  <button
                    className="touch-button top-new-button"
                    type="button"
                    onClick={() => handleCreateItem()}
                  >
                    <AddCircleOutlineIcon /> NEW ITEM
                  </button>
                  <button
                    className="icon-button"
                    type="button"
                    aria-label="Open settings"
                    onClick={handleOpenSettings}
                  >
                    <SettingsIcon />
                  </button>
                  {debugEnabled && (
                    <button
                      className="icon-button"
                      type="button"
                      aria-label="Open diagnostics"
                      onClick={() => setModalMode("debug")}
                    >
                      <BugReportIcon />
                    </button>
                  )}
                </div>

                {error && (
                  <div className="empty-state" role="alert">
                    {error}
                  </div>
                )}

                <section
                  className={`modify-grid ${visibleActiveItem ? "" : "compact-only"}`}
                  aria-label="Modify pallet locations"
                >
                  <ItemList
                    items={items}
                    activeItemId={visibleActiveItem ? visibleActiveItem.id : null}
                    loading={loading}
                    highlightedItemId={highlightedItemId}
                    onSelect={handleSelectItem}
                    onDelete={handleRequestDeleteItem}
                  />
                  {visibleActiveItem && (
                    <ActiveItemPanel
                      activeItem={visibleActiveItem}
                      locations={activeItemLocations}
                      activeLocationId={activeLocationId}
                      highlightedLocationId={highlightedLocationId}
                      editSignal={editSignal}
                      panelRef={activePanelRef}
                      onRenameItem={handleRenameItem}
                      onOpenHistoricNames={() => openHistoricNames("rename")}
                      onNewLocation={handleNewLocation}
                      onDeleteLocation={(locationRecord) => {
                        setSelectedLocation(locationRecord);
                        setModalMode("remove");
                      }}
                      onEditProdNeeded={() => setModalMode("prod-needed")}
                      onSelectLocation={(locationRecord) => {
                        setActiveLocationId(locationRecord.id);
                        setSelectedLocation(locationRecord);
                        setModalMode("actions");
                      }}
                    />
                  )}
                </section>

                {pickerOpen && visibleActiveItem && (
                  <div ref={pickerRef}>
                    <LocationPicker
                      activeItem={visibleActiveItem}
                      selectedRow={selectedRow}
                      selectedBay={selectedBay}
                      isHardline={locationIsHardline}
                      initialTab={pickerInitialTab}
                      rowButtonsPerRow={rowButtonsPerRow}
                      bayButtonsPerRow={bayButtonsPerRow}
                      saveDisabled={saveDisabled}
                      onSelectRow={(row) => {
                        setSelectedRow(row);
                        setSelectedBay(null);
                        setPickerInitialTab("bay");
                      }}
                      onSelectBay={setSelectedBay}
                      onHardlineChange={setLocationIsHardline}
                      onSave={handleSaveLocation}
                    />
                  </div>
                )}
              </>
            }
          />
          <Route
            path="/all"
            element={
              <ViewAll
                items={items}
                locations={locations}
                loading={loading}
                showEmptyRows={showEmptyRows}
                onModify={handleModify}
                onSettings={handleOpenSettings}
                onSelectLocation={(locationRecord) => {
                  setActiveLocationId(locationRecord.id);
                  setSelectedLocation(locationRecord);
                  setModalMode("actions");
                }}
                onQuickDropped={(locationRecord) =>
                  handlePatchLocation(
                    locationRecord.id,
                    { is_dropped: !locationRecord.is_dropped },
                    locationRecord.is_dropped
                      ? "Dropped marker cleared"
                      : "Location marked dropped"
                  )
                }
                onQuickNote={(locationRecord) => {
                  setSelectedLocation(locationRecord);
                  setModalMode("note");
                }}
                onQuickPartial={(locationRecord) => {
                  setSelectedLocation(locationRecord);
                  setModalMode("partial");
                }}
                onQuickRemove={(locationRecord) => {
                  setSelectedLocation(locationRecord);
                  setModalMode("remove");
                }}
              />
            }
          />
        </Routes>
      </div>

      {modalMode === "actions" && selectedLocation && (
        <ActionModal
          location={selectedLocation}
          onClose={() => {
            setSelectedLocation(null);
            setModalMode(null);
          }}
          onDropped={() =>
            handlePatchLocation(
              selectedLocation.id,
              { is_dropped: !selectedLocation.is_dropped },
              selectedLocation.is_dropped
                ? "Dropped marker cleared"
                : "Location marked dropped"
            ).then((updated) => {
              if (updated) {
                setSelectedLocation(null);
                setModalMode(null);
              }
            })
          }
          onNote={() => setModalMode("note")}
          onPartial={() => setModalMode("partial")}
          onRemove={() => setModalMode("remove")}
        />
      )}

      {modalMode === "note" && selectedLocation && (
        <NoteModal
          location={selectedLocation}
          onCancel={() => setModalMode("actions")}
          onSave={async (note) => {
            await handlePatchLocation(selectedLocation.id, { note }, "Note saved");
            setModalMode("actions");
          }}
        />
      )}

      {modalMode === "partial" && selectedLocation && (
        <PartialModal
          location={selectedLocation}
          onCancel={() => setModalMode("actions")}
          onSave={async (partialCount) => {
            await handlePatchLocation(
              selectedLocation.id,
              { partial_count: partialCount },
              "Partial count saved"
            );
            setModalMode("actions");
          }}
        />
      )}

      {modalMode === "prod-needed" && visibleActiveItem && (
        <ProdNeededModal
          item={visibleActiveItem}
          onCancel={() => setModalMode(null)}
          onSave={async (prodNeeded) => {
            const item = await handleUpdateProdNeeded(prodNeeded);
            if (item) {
              setModalMode(null);
            }
          }}
        />
      )}

      {modalMode === "remove" && selectedLocation && (
        <ConfirmModal
          title="Remove this location?"
          message={`This will remove ${formatLocationLong(selectedLocation)} from ${selectedLocation.item_name}.`}
          confirmLabel="Remove"
          onCancel={() => setModalMode("actions")}
          onConfirm={handleRemoveLocation}
        />
      )}

      {modalMode === "delete-item" && pendingDeleteItem && (
        <ConfirmModal
          title={`Delete ${pendingDeleteItem.item_name}?`}
          message="This deletes the item and all saved locations for that item. This cannot be undone."
          confirmLabel="Delete item"
          onCancel={() => {
            setPendingDeleteItem(null);
            setModalMode(null);
          }}
          onConfirm={() => handleDeleteItem(pendingDeleteItem)}
        />
      )}

      {modalMode === "delete-all" && (
        <ConfirmModal
          title="Delete all items?"
          message="This deletes every item and all saved locations. This cannot be undone."
          confirmLabel="Delete all"
          onCancel={() => setModalMode(null)}
          onConfirm={handleDeleteAllItems}
        />
      )}

      {modalMode === "delete-all-locations" && (
        <ConfirmModal
          title="Delete all locations?"
          message="This removes every saved location but keeps your item names. This cannot be undone."
          confirmLabel="Delete locations"
          onCancel={() => setModalMode(null)}
          onConfirm={handleDeleteAllLocations}
        />
      )}

      {modalMode === "added-location" && addedLocation && (
        <AddedLocationModal
          location={addedLocation}
          onClose={closeAddedLocationModal}
          onAddNewItem={handleAddNewItem}
          onAddTemplateItem={() => openHistoricNames("create")}
          onAddNewRow={handleAddAnotherRow}
          onAddNewBay={handleAddAnotherBay}
        />
      )}

      {modalMode === "historic-names" && (
        <HistoricItemNamesModal
          names={historicItemNames}
          loading={historicNamesLoading}
          onClose={closeHistoricNames}
          onDelete={handleDeleteHistoricName}
          onSelect={handleSelectHistoricName}
        />
      )}

      {modalMode === "settings" && (
        <SettingsModal
          rowButtonsPerRow={rowButtonsPerRow}
          bayButtonsPerRow={bayButtonsPerRow}
          showEmptyRows={showEmptyRows}
          hasItems={items.length > 0}
          hasLocations={locations.length > 0}
          debugEnabled={debugEnabled}
          onClose={() => setModalMode(null)}
          onImport={() => setModalMode("import")}
          onDeleteAllItems={() => setModalMode("delete-all")}
          onDeleteAllLocations={() => setModalMode("delete-all-locations")}
          onRowButtonsPerRowChange={setRowButtonsPerRow}
          onBayButtonsPerRowChange={setBayButtonsPerRow}
          onShowEmptyRowsChange={setShowEmptyRows}
          onDebug={() => setModalMode("debug")}
        />
      )}

      {modalMode === "import" && (
        <ImportModal
          onCancel={() => setModalMode(null)}
          onImport={handleImportCsv}
        />
      )}

      {modalMode === "debug" && debugEnabled && (
        <DebugDiagnosticsModal onClose={() => setModalMode(null)} />
      )}

      <Toast message={toast} />
    </main>
  );
}

export default function App() {
  return (
    <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
      <AppShell />
    </BrowserRouter>
  );
}
